/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage, useIntl } from 'react-intl'

import {
  SelectInput,
  DecimalInput,
  isNumber,
} from '../Inputs/Inputs'

import RedX from '../Graphics/RedX'

import AppDispatcher from '../../../dispatchers/AppDispatcher'

import styles from '../../../../css/settings.module.css'

function Simple (props) {
  const {
    body,
    debounceTime,
    formula,
    invalid = false,
    locale,
    multiplier,
    notificationInterval,
    sendOnce,
    subject,
    template,
    test,
    testValue,
    testValueDisplay = (arg) => arg,
    testValueDisplayReverse = (arg) => arg,
    testValueRequired,
    type,
    valueKey,
    sensor,
  } = props

  const [state, setState] = useState({
    body,
    debounceTime,
    formula,
    invalid,
    multiplier,
    notificationInterval,
    sendOnce,
    subject,
    template,
    test,
    testValue,
    testValueValidation: testValueDisplay(testValue, locale, 1),
    type,
    valueKey,
    sensor,
  })

  const intl = useIntl()
  const { formatMessage } = intl

  const updateNewRule = useCallback(() => {
    const {
      body,
      debounceTime,
      formula,
      invalid,
      multiplier,
      notificationInterval,
      sendOnce,
      subject,
      template,
      test,
      testValue,
      type,
      valueKey,
      sensor,
    } = state

    AppDispatcher.dispatch({
      action: 'update-new-rule-parameters',
      body,
      debounceTime,
      formula,
      invalid,
      multiplier,
      notificationInterval,
      sendOnce,
      subject,
      template,
      test,
      testValue,
      type,
      valueKey,
      sensor,
    })
  }, [state])

  const handleChange = useCallback((field, value, callback) => {
    let valueU = value
    if (field === 'sendOnce') {
      valueU = value === 'true'
    }

    setState((prevState) => ({
      ...prevState,
      [field]: valueU,
    }))

    if (callback) callback()
  }, [])

  const handleChangeInterval = useCallback((field, value, callback) => {
    setState((prevState) => ({
      ...prevState,
      [field]: Number(value),
    }))

    if (callback) callback()
  }, [])

  const handleChangeTestValue = useCallback((field, value, callback) => {
    const valueNumber = Number(value)
    const valueCorrect = testValueRequired ? isFinite(parseFloat(value)) : isNumber(valueNumber)

    if (valueCorrect) {
      setState((prevState) => ({
        ...prevState,
        [field]: testValueDisplayReverse(valueNumber, locale, 2),
        invalid: false,
        testValueInvalid: false,
        testValueValidation: value,
      }))
    } else {
      setState((prevState) => ({
        ...prevState,
        invalid: true,
        testValueInvalid: true,
        testValueValidation: value,
      }))
    }

    if (callback) callback()
  }, [testValueRequired, testValueDisplayReverse, locale])

  const childChange = useCallback((event) => {
    switch (event.target.name) {
      case 'testValue':
        handleChangeTestValue(event.target.name, event.target.value)
        break
      case 'test':
      case 'valueKey':
      case 'method':
      case 'sendOnce':
        handleChange(event.target.name, event.target.value)
        break
      case 'notificationInterval':
        handleChangeInterval(event.target.name, event.target.value)
        break
      default:
        break
    }
  }, [handleChange, handleChangeInterval, handleChangeTestValue])

  useEffect(() => {
    updateNewRule()
  }, [state, updateNewRule])

  const {
    notificationInterval: stateNotificationInterval,
    sendOnce: stateSendOnce,
    testValueValidation,
    testValueInvalid,
  } = state

  let testDisplay = 'less than'

  switch (test) {
    case 'averageLessThan':
      testDisplay = formatMessage({
        id: 'rules.general.averageIsLessThan',
        defaultMessage: 'average is less than',
        description: 'average is less than label',
      })
      break

    case 'averageGreaterThan':
      testDisplay = formatMessage({
        id: 'rules.general.averageIsGreaterThan',
        defaultMessage: 'average is greater than',
        description: 'average is greater than label',
      })
      break

    case 'greaterThan':
      testDisplay = formatMessage({
        id: 'rules.general.isGreaterThan',
        defaultMessage: 'is greater than',
        description: 'isGreaterThan text',
      })
      break

    case 'equalTo':
      testDisplay = formatMessage({
        id: 'rules.general.isEqualTo',
        defaultMessage: 'is equal to',
        description: 'isEqualTo text',
      })
      break

    case 'lessThan':
    default:
      testDisplay = formatMessage({
        id: 'rules.general.isLessThan',
        defaultMessage: 'is less than',
        description: 'isLessThan text',
      })
      break
  }

  return (
    <div>
      <section>
        <FormattedMessage
          id='rules.alertWhen'
          defaultMessage='Alert me when: {sensor} {testDisplay}'
          description='Alert when heading'
          tagName='h5'
          values={{
            sensor,
            testDisplay,
          }}
        />
        <div className={styles.ruleDecimal} style={{ position: 'relative' }}>
          <DecimalInput
            value={testValueValidation}
            childChange={childChange}
            field='testValue'
            style={styles.ruleDecimalInput}
          />
          {testValueInvalid && (
            <RedX
              style={{
                position: 'absolute',
                bottom: '8px',
                right: '16px',
                height: '24px',
                width: '24px',
              }}
            />
          )}
        </div>
      </section>
      <section>
        <FormattedMessage
          id='rules.snooze.name'
          defaultMessage='Snooze Alerts'
          description='Snooze Alerts heading'
          tagName='h5'
        />
        <div className={styles.ruleSelect}>
          <SelectInput
            value={stateNotificationInterval}
            field='notificationInterval'
            childChange={childChange}
            options={[
              {
                value: 0,
                name: formatMessage({
                  id: 'rules.reminders.never',
                  defaultMessage: 'None',
                  description: 'None option',
                }),
              },
              {
                value: 900000,
                name: formatMessage({
                  id: 'rules.reminders.every15Min',
                  defaultMessage: '15 minutes',
                  description: '15 minutes option',
                }),
              },
              {
                value: 3600000,
                name: formatMessage({
                  id: 'rules.reminders.every1Hrs',
                  defaultMessage: '1 hour',
                  description: '1 hour option',
                }),
              },
              {
                value: 14400000,
                name: formatMessage({
                  id: 'rules.reminders.every4Hrs',
                  defaultMessage: '4 hours',
                  description: '4 hours option',
                }),
              },
              {
                value: 64800000,
                name: formatMessage({
                  id: 'rules.reminders.every18Hrs',
                  defaultMessage: '18 hours',
                  description: '18 hours option',
                }),
              },
              {
                value: 86400000,
                name: formatMessage({
                  id: 'rules.reminders.every24Hrs',
                  defaultMessage: '24 hours',
                  description: '24 hours option',
                }),
              },
            ]}
            style={styles.ruleSelectInput}
          />
        </div>
      </section>
      <section>
        <FormattedMessage
          id='rules.reminders.name'
          defaultMessage='Send Reminders'
          description='Send Reminders heading'
          tagName='h5'
        />
        <div className={styles.ruleSelect}>
          <SelectInput
            value={stateSendOnce}
            field='sendOnce'
            childChange={childChange}
            options={[
              {
                value: true,
                name: formatMessage({
                  id: 'rules.sendOnce.off',
                  defaultMessage: 'Off',
                  description: 'Off option',
                }),
              },
              {
                value: false,
                name: formatMessage({
                  id: 'rules.sendOnce.on',
                  defaultMessage: 'On',
                  description: 'On option',
                }),
              },
            ]}
            style={styles.ruleSelectInput}
          />
        </div>
      </section>
    </div>
  )
}

Simple.propTypes = {
  rule: PropTypes.object,
}

export default Simple
